import { Callout, Related } from "./parts";

export function UsageAdjustments() {
  return (
    <>
      <p>
        A usage adjustment changes what a customer is charged for a month without touching the recorded usage. Every
        adjustment has a reason. Add one from the customer's <strong>Billing</strong> tab (<strong>Add usage
        adjustment</strong>) or from a month's invoice. It reaches the invoice only once it is approved.
      </p>

      <h2>Kinds</h2>
      <table>
        <thead>
          <tr>
            <th>Kind</th>
            <th>You give</th>
            <th>Effect</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Credit</td>
            <td>An amount</td>
            <td>Taken off the invoice at face value, after the minimum and fee.</td>
          </tr>
          <tr>
            <td>Write-off</td>
            <td>A model and token type</td>
            <td>Waives the price of that usage for the month. Its cost stays on the invoice.</td>
          </tr>
          <tr>
            <td>Corrected tokens</td>
            <td>A model, token type, a token count (can be negative) and the month the tokens belong to</td>
            <td>
              Adds or removes tokens, priced at the rate that applied on the last day of that month. A negative
              correction is a credit.
            </td>
          </tr>
        </tbody>
      </table>
      <p>
        Example: a customer reports a runaway script on 12 August. You add a $250 credit to the open September month with
        the reason “Runaway script on 12 Aug, agreed with finance”. After approval, September's invoice shows a −$250
        credit line.
      </p>

      <h2 id="approving">Approving</h2>
      <ol>
        <li>
          <strong>Create</strong> (billing admin): the adjustment is a <em>draft</em> and is not on any invoice.
        </li>
        <li>
          <strong>Approve</strong> (billing admin): one approval is enough, and it may be the person who created it. The
          audit log records who created and who approved it, with the reason and amounts; that record is the control.
        </li>
        <li>
          <strong>Applied</strong>: the close of its month puts it on the invoice.
        </li>
      </ol>
      <p>
        <strong>Void</strong> removes an adjustment any time before it is applied. After the month closes it can no longer
        be voided; add an opposite adjustment to a later month instead.
      </p>
      <Callout>
        Only approved adjustments reach an invoice. A draft someone forgot to approve is left off, and after the close it
        can only go on a later month. Check for drafts before closing.
      </Callout>

      <h2>Which month</h2>
      <p>
        An adjustment targets an <strong>open</strong> month, by default the oldest open one. A closed invoice never
        changes: to correct a closed month, add corrected tokens naming that month as the source, or a credit, to an open
        month.
      </p>

      <Related to={["closing-a-month", "trials-and-credits", "audit-log"]} />
    </>
  );
}
